<!-- eslint-disable vue/first-attribute-linebreak -->
<!--
  Ported from pawnshop-admin-new/src/views/customers/NewCustomerHistory.vue (2026-09-04).
  This app has neither @vuepic/vue-datepicker nor vue-awesome-paginate as dependencies (it's the
  slim customer-insight-only frontend — no views/generals, no examples/ExampleOne.vue), so two
  pieces are reimplemented natively instead of copied verbatim:
    - date range: native <input type="date"> (matches this app's own `type="month"` convention
      already used in CustomerInsight.vue's day-level growth picker) instead of VueDatePicker.
    - pagination: a small hand-rolled Prev/number/Next control instead of <vue-awesome-paginate>,
      styled with the *same* CSS class names (.example-one, .paginate-buttons, .active-page, …)
      copied from @/examples/ExampleOne.vue in the other two frontends, so it looks identical even
      though there's no library underneath.
  Everything else (filters, table, totals, store calls) is unchanged.
-->
<template>
  <div class="container-fluid">
    <div v-if="loading" class="loading-state">
      <ProgressSpinner fill="transparent" aria-label="Loading" />
    </div>

    <div class="row mb-5">
      <div class="col-12">
        <div class="card mt-2">
          <div class="card-header mb-0">
            <h5>{{ $t("apps.newCustomerHistory") }}</h5>
          </div>

          <div class="card-body pt-0">
            <!-- Filter — one row: start date · end date · main category · pawn value -->
            <div class="row">
              <div class="col-lg-3 mt-2">
                <label class="form-label ms-0">{{ $t("content.sdate") }}</label>
                <!-- <input v-model="start_date" type="date" class="form-control" :max="todayStr"> -->
                <VueDatePicker v-model="start_date" :format="format" auto-apply :teleport="true" :max-date="new Date()"
                  :enable-time-picker="false" month-name-format="long" :clearable="false" :text-input="textInputOptions"
                  fluid />
              </div>

              <div class="col-lg-3 mt-2">
                <label class="form-label ms-0">{{ $t("content.edate") }}</label>
                <!-- <input v-model="end_date" type="date" class="form-control" :max="todayStr"> -->
                <VueDatePicker v-model="end_date" :format="format" auto-apply :teleport="true" :max-date="new Date()"
                  :enable-time-picker="false" month-name-format="long" :clearable="false" :text-input="textInputOptions"
                  fluid />
              </div>

              <div class="col-lg-2 mt-2">
                <label class="form-label ms-0">{{ $t("content.masset") }}</label>
                <select v-model="assetType" class="form-select">
                  <option value="">{{ $t("content.optall") }}</option>
                  <option v-for="(at, i) in assetTypeOptions" :key="i" :value="at">{{ at }}</option>
                </select>
              </div>

              <div class="col-lg-4 mt-2">
                <label class="form-label ms-0">{{ $t("content.amnpawnval") }}</label>
                <div class="input-group">
                  <select v-model="pawnOp" class="form-select group-select" style="flex-grow: 0.5"
                    @change="pawnValue = 0; toPawnValue = 0">
                    <option value="">{{ $t("content.optall") }}</option>
                    <option v-for="(op, i) in pawnOperators" :key="i" :value="op.val">{{ op.lb }}</option>
                  </select>
                  <input :value="pawnDisplay(pawnValue)" type="text" inputmode="numeric"
                    class="form-control" style="flex-grow: 1" :disabled="pawnOp === ''"
                    @input="onPawnInput($event, 'pawnValue')" @focus="$event.target.select()">
                  <template v-if="pawnOp === 'BETWEEN'">
                    &nbsp;-&nbsp;
                    <input :value="pawnDisplay(toPawnValue)" type="text" inputmode="numeric"
                      class="form-control" style="flex-grow: 1"
                      @input="onPawnInput($event, 'toPawnValue')" @focus="$event.target.select()">
                  </template>
                </div>
              </div>
            </div>

            <div class="row mt-4">
              <div class="col-12 d-flex justify-content-end align-items-center">
                <div class="me-3">
                  <material-button id="cardSearch" color="primary" class="float-lg-start float-sm-end mb-3"
                    :disabled="loading || invalidRange || invalidPawnRange" @click="onSearch">
                    <i class="material-icons material-symbols-outlined">search</i>
                    {{ $t("buttons.sear") }}
                  </material-button>
                </div>
                <div class="me-3">
                  <material-button id="cardReset" color="danger" class="float-lg-start float-sm-end mb-3"
                    :disabled="loading" @click="cancelBtn">
                    <i class="material-icons material-symbols-outlined">close</i>
                    {{ $t("buttons.canc") }}
                  </material-button>
                </div>
              </div>
            </div>

            <p v-if="invalidRange" class="text-sm text-danger ms-1">
              วันเริ่มต้นต้องไม่มากกว่าวันสิ้นสุด
            </p>
            <p v-if="invalidPawnRange" class="text-sm text-danger ms-1">
              มูลค่าจำนำเริ่มต้นต้องไม่มากกว่าค่าสิ้นสุด
            </p>
            <p v-if="errorMsg" class="text-sm text-danger ms-1">{{ errorMsg }}</p>

            <hr />

            <!-- Table -->
            <div class="row mt-3">
              <div class="table-responsive">
                <table class="table table-flush">
                  <thead class="thead-light">
                    <tr>
                      <th>#</th>
                      <th>{{ $t("content.dant") }}</th>
                      <th>{{ $t("content.names") }}</th>
                      <th>{{ $t("content.ticketno") }}</th>
                      <th>{{ $t("content.stockno") }}</th>
                      <th class="text-center">{{ $t("content.trans") }}</th>
                      <th class="text-center">{{ $t("content.masset") }}</th>
                      <th>{{ $t("content.amnpawnvalall") }}</th>
                      <th>{{ $t("content.amnduedate") }}</th>
                      <th class="text-center">{{ $t("content.status") }}</th>
                      <th>{{ $t("content.act") }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(row, index) in rows" :key="index">
                      <td class="text-sm font-weight-normal">
                        {{ formatNumberString((currentPage - 1) * perPage + index + 1) }}
                      </td>
                      <td class="text-sm font-weight-normal">{{ formatWhen(row.transactionDate) }}</td>
                      <td class="text-sm font-weight-normal">{{ row.customerName }}</td>
                      <td class="text-sm font-weight-normal">{{ row.ticketNo || "-" }}</td>
                      <td class="text-sm font-weight-normal">{{ row.stockNo || "-" }}</td>
                      <td class="text-sm font-weight-normal text-center">{{ txLabel(row.transactionType) }}</td>
                      <td class="text-sm font-weight-normal text-center">{{ row.assetType }}</td>
                      <td class="text-sm font-weight-normal">{{ formatNumberDecimalString(row.newPrincipal) }}</td>
                      <td class="text-sm font-weight-normal">{{ format_date(row.dueDate) || "-" }}</td>
                      <td class="text-sm font-weight-normal text-center">{{ statusLabel(row.chainStatus) }}</td>
                      <td class="text-sm font-weight-normal">
                        <material-button color="info" variant="outline" class="rounded-circle"
                          :title="t('buttons.seed')" @click="openDetail(row)">
                          <i class="material-icons material-symbols-outlined">visibility</i>
                        </material-button>
                      </td>
                    </tr>
                    <tr v-if="rows.length === 0">
                      <td colspan="10" class="text-sm font-weight-normal">
                        <p style="text-align: center">No Record Found</p>
                      </td>
                    </tr>
                  </tbody>
                  <tfoot>
                    <tr class="group-sub-header">
                      <td class="text-lg font-weight-bold text-end text-center" colspan="7">
                        <span v-if="rows.length > 0"><b>{{ $t("content.gtotal") }}</b></span>
                      </td>
                      <td class="text-lg font-weight-bold">
                        <span v-if="rows.length > 0">
                          <b>{{ formatNumberDecimalString(totals.openingPrincipal) }}</b>
                        </span>
                      </td>
                      <td class="text-lg font-weight-bold" colspan="3" style="text-align: right">
                        ลูกค้าใหม่ทั้งหมด : {{ formatNumberString(totals.newCustomers) }}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            <pre></pre>
            <!-- Hand-rolled pager (no vue-awesome-paginate in this app) — see file header. -->
            <div style="display: flex; justify-content: flex-end">
              <ul v-show="rows.length > 0" class="pagination justify-content-end">
                <div class="project-container">
                  <div class="example-one">
                    <div class="pagination-container">
                      <button type="button" class="paginate-buttons back-button"
                        :disabled="currentPage === 1" @click="goToPage(currentPage - 1)">&lt;</button>
                      <template v-for="(p, i) in pageNumbers" :key="i">
                        <span v-if="p === '...'" class="paginate-ellipsis">…</span>
                        <button v-else type="button" class="paginate-buttons"
                          :class="{ 'active-page': p === currentPage }" @click="goToPage(p)">{{ p }}</button>
                      </template>
                      <button type="button" class="paginate-buttons next-button"
                        :disabled="currentPage === totalPages" @click="goToPage(currentPage + 1)">&gt;</button>
                    </div>
                  </div>
                </div>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Detail Modal -->
  <TicketDetailModal 
  v-if="workcaseDetail !== null"
  :workcaseDetail="workcaseDetail"
  :images="images"
  :listinfo="listinfo"
  :paymentE="paymentE"
  :paymentL="payment_l"
  :promotionDetail="promotionDetail"
  :depRef="dep_ref"
  :tkStatus="tk_status"
  :intCal="int_cal"
  :intPeriod="int_period"
  :transactionLabel="checkTransaction(workcaseDetail.transactionType)"
  :history="'no'" 
  v-model:fcusdt="fcusdt" 
  :afChangename=af_changename
  :bfChangename=bf_changename
  :transDatePrv=transdate_prv
  @copy="copyBtn"
  @openHistory="NewTab"/>

  <!-- วางไว้ท้ายไฟล์นอกตาราง -->
  <material-button color="info" variant="outline" class="rounded-circle" v-show="false"
                  data-bs-toggle="modal" data-bs-target="#lookupModal2" id="detailModal">
  </material-button>


  </div>
</template>

<script setup>
/* eslint-disable no-unused-vars */
import { ref, computed, onMounted, nextTick } from "vue";
import { useI18n } from "vue-i18n";
import moment from "moment";
import ProgressSpinner from "primevue/progressspinner";
import MaterialButton from "@/components/MaterialButton.vue";
import TicketDetailModal from '@/components/TicketDetailModal.vue';
import { VueImageZoomer } from 'vue-image-zoomer'
import 'vue-image-zoomer/dist/style.css';
import noImg from "@/assets/img/noimg.png"
import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css'


import { useInsightStore } from "@/stores/insight";
import { useWorkCaseStore } from '@/stores/workcase'
import { useInterestStore } from '@/stores/interest'
import { usePaymentGroupStore } from '@/stores/paymentgroup'
import { useDocumentStore } from '@/stores/document'
import { useDepositStore } from '@/stores/deposit'

const { t } = useI18n();
const store = useInsightStore();
const storewc = useWorkCaseStore()
const storei = useInterestStore()
const storep = usePaymentGroupStore()
const stored = useDocumentStore()
const storedepo = useDepositStore()

const TX_LABEL = {
  PAWN: "จำนำ",
  RENEW: "ต่อดอก",
  INCREASE: "เพิ่มต้น",
  REDUCE: "ลดต้น",
  REDEEM: "ไถ่ถอน",
  FORFEIT: "ทรัพย์หลุด",
};
const STATUS_LABEL = {
  OPEN: "เปิดอยู่",
  REDEEMED: "ไถ่ถอนแล้ว",
  FORFEITED: "ทรัพย์หลุด",
  TRANSFERRED: "ย้ายระบบ",
};
const txLabel = (v) => TX_LABEL[v] || v || "-";
const statusLabel = (v) => STATUS_LABEL[v] || v || "-";

// Native date inputs work in plain "yyyy-MM-dd" strings already — no datepicker lib to format for.
const todayStr = moment().format("YYYY-MM-DD");
const start_date = ref(todayStr);
const end_date = ref(todayStr);

// มูลค่าจำนำ filter — same operators/UX as pawnshop-admin-new's Stock tab (stocks/stock.vue).
const pawnOperators = [
  { val: "EQUAL", lb: "เท่ากับ" },
  { val: "MORE_THAN", lb: "มากกว่า" },
  { val: "LESS_THAN", lb: "น้อยกว่า" },
  { val: "BETWEEN", lb: "ระหว่าง" },
];
const pawnOp = ref("");
const pawnValue = ref(0);
const toPawnValue = ref(0);

// ประเภทหลัก filter — options come from insightApi.assetTypes() (distinct ticket_chains.asset_type).
const assetType = ref("");
const assetTypeOptions = ref([]);

const perPage = ref(50);
const currentPage = ref(1);
const total = ref(0);
const rows = ref([]);
const totals = ref({ newCustomers: 0, openingPrincipal: 0, openingInterest: 0 });
const loading = ref(false);
const errorMsg = ref("");

const workcaseDetail = ref(null);
const tk_status = ref('');
const int_cal = ref(0);
const paymentE = ref('');
const images = ref([])
const images_l = ref([])
const payment_l = ref([])
const int_period = ref('');
const fcusdt = ref(false);
const dep_ref = ref('');
const promotionDetail = ref(null);
const bf_changename = ref('');
const af_changename = ref('');
const transdate_prv = ref(null);
const listinfo = ref([]);

const key = JSON.parse(localStorage.getItem("shop_seleted"));

const invalidRange = computed(() => {
  if (!start_date.value || !end_date.value) return true;
  return start_date.value > end_date.value;
});

const invalidPawnRange = computed(() =>
  pawnOp.value === "BETWEEN" && Number(pawnValue.value) > Number(toPawnValue.value)
);

const totalPages = computed(() => Math.max(1, Math.ceil(total.value / perPage.value)));

const format = (date) => {
  return moment(String(date)).local().format('DD/MM/YYYY');
}

const textInputOptions = {
  format: 'dd/MM/yyyy'
};

// Windowed page list (current ± 2, plus first/last with "…" gaps) so a large result set doesn't
// render hundreds of page buttons — same idea vue-awesome-paginate uses in the other two frontends.
const pageNumbers = computed(() => {
  const tp = totalPages.value;
  const cur = Math.min(currentPage.value, tp);
  const delta = 2;
  const range = [];
  for (let i = Math.max(1, cur - delta); i <= Math.min(tp, cur + delta); i++) range.push(i);
  if (range[0] > 1) {
    if (range[0] > 2) range.unshift("...");
    range.unshift(1);
  }
  if (range[range.length - 1] < tp) {
    if (range[range.length - 1] < tp - 1) range.push("...");
    range.push(tp);
  }
  return range;
});

function goToPage(p) {
  if (p === "..." || p < 1 || p > totalPages.value || p === currentPage.value) return;
  currentPage.value = p;
  loadData();
}

// pawn-value inputs: number ref is the source of truth (sent to the API); the field shows it with
// thousands separators as the user types.
function pawnDisplay(n) {
  return n ? formatNumberString(n) : "";
}

function onPawnInput(e, field) {
  const target = field === "pawnValue" ? pawnValue : toPawnValue;
  const digits = e.target.value.replace(/\D/g, "");
  target.value = digits === "" ? 0 : Number(digits);
  e.target.value = digits === "" ? "" : formatNumberString(Number(digits));
}

function format_date(value) {
  if (value) return moment(String(value)).local().format("DD/MM/YYYY");
  return "";
}

// transactionDate is "yyyy-MM-dd'T'HH:mm:ss" (Bangkok, from raw_upload_rows.tx_datetime) when the
// transaction's time-of-day is known, or plain "yyyy-MM-dd" when it isn't.
function formatWhen(value) {
  if (!value) return "";
  const s = String(value);
  return moment(s).format(s.includes("T") ? "DD/MM/YYYY HH:mm:ss" : "DD/MM/YYYY");
}

function formatNumberString(num) {
  return String(num).replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1,");
}

function formatNumberDecimalString(num) {
  const n = num !== null && num !== undefined ? Number(num) : 0;
  return n.toFixed(2).replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1,");
}

function buildFilters() {
  const f = {};
  if (pawnOp.value) {
    f.pawnValueOperator = pawnOp.value;
    f.pawnValue = Number(pawnValue.value) || 0;
    if (pawnOp.value === "BETWEEN") f.toPawnValue = Number(toPawnValue.value) || 0;
  }
  if (assetType.value) f.assetType = assetType.value;
  return f;
}

async function loadAssetTypes() {
  try {
    assetTypeOptions.value = (await store.fetchInsightAssetTypes()) || [];
  } catch (_e) {
    assetTypeOptions.value = [];
  }
}

async function loadData() {
  if (invalidRange.value || invalidPawnRange.value) return;
  loading.value = true;
  errorMsg.value = "";
  try {
    
    const formattedStartDate = moment(start_date.value, ["DD/MM/YYYY", "YYYY-MM-DD"]).format("YYYY-MM-DD");
    const formattedEndDate = moment(end_date.value, ["DD/MM/YYYY", "YYYY-MM-DD"]).format("YYYY-MM-DD");

    const res = await store.fetchNewCustomerHistory(
      formattedStartDate, 
      formattedEndDate, 
      currentPage.value - 1, 
      perPage.value, 
      buildFilters()
    );
    
    // บรรทัดที่มีการแก้ไข 3 บรรทัดหลัก:
    rows.value = res.data || [];
    total.value = res.page?.total || 0;
    totals.value = res.totals || { newCustomers: 0, openingPrincipal: 0, openingInterest: 0 };
    //console.log("loadData: rows.length=" + rows.value.length + ", total=" + total.value);
  } catch (e) {
    rows.value = [];
    total.value = 0;
    totals.value = { newCustomers: 0, openingPrincipal: 0, openingInterest: 0 };
    errorMsg.value = e?.message ? `โหลดข้อมูลไม่สำเร็จ: ${e.message}` : "โหลดข้อมูลไม่สำเร็จ";
  } finally {
    loading.value = false;
  }
}


function onSearch() {
  currentPage.value = 1;
  loadData();
}

function cancelBtn() {
  start_date.value = todayStr;
  end_date.value = todayStr;
  pawnOp.value = "";
  pawnValue.value = 0;
  toPawnValue.value = 0;
  assetType.value = "";
  currentPage.value = 1;
  rows.value = [];
  total.value = 0;
  totals.value = { newCustomers: 0, openingPrincipal: 0, openingInterest: 0 };
  errorMsg.value = "";
}

async function openDetail(item) {
  workcaseDetail.value = null
  images.value = []
  images_l.value = []
  listinfo.value = []
  tk_status.value = ''
  int_cal.value = 0
  paymentE.value = ''
  int_period.value = ''
  fcusdt.value = false
  dep_ref.value = ''
  bf_changename.value = ''
  af_changename.value = ''
  transdate_prv.value = null
  promotionDetail.value = null

  await getworkCaseInfo(item.ticketNo)
  if (workcaseDetail.value != null) {

    if (workcaseDetail.value.changeName ) {
      af_changename.value = item.cusName
      getworkCaseInfoPrv(workcaseDetail.value.prevRefNumber)
    }else { 
      if (workcaseDetail.value.transactionType !== 'PAWN') { 
          getworkCaseInfoPrv(workcaseDetail.value.prevRefNumber)
      }
    }

    if (workcaseDetail.value.deposit) {
      getDepositInfo(workcaseDetail.value.tkRefNumber)
    }
    detailPayment(workcaseDetail.value.paymentGroupUid)

    images_l.value = workcaseDetail.value.ticket.images
    listinfo.value = workcaseDetail.value.ticket.collaterals

    if (workcaseDetail.value.promotionUid !== '0') { 
          retrieveInfo(workcaseDetail.value)
    }

    if (workcaseDetail.value.transactionType !== 'REDEEM') {
      if(workcaseDetail.value.latest){
        calIntInt()
      }
      if (workcaseDetail.value.status === 'FINISH') {
        tk_status.value = 'ปกติ'
      } else if (workcaseDetail.value.status === 'FREEZE') {
        tk_status.value = 'อายัด'
      } else if (workcaseDetail.value.status === 'TERMINATE') {
        tk_status.value = 'ยกเลิก'
      } else if (workcaseDetail.value.status === 'ACTIVE') {
        tk_status.value = 'กำลังดำเนินการ'
      } else if (workcaseDetail.value.status === 'BREAKING') {
        tk_status.value = 'ของหลุด'
      }

    } else {
      if (workcaseDetail.value.status === 'FINISH') {
        tk_status.value = 'ไถ่ถอน'
      } else if (workcaseDetail.value.status === 'TERMINATE') {
        tk_status.value = 'ยกเลิก'
      }
    }
    // 🟢 1. ตรวจสอบรายการไฟล์แนบจาก images_l.value
    if (images_l.value.length > 0) {
      
      // 🟢 ไฮไลท์จุดแก้: ต้องมี `await` เพื่อบังคับรอให้ Promise.all โหลดล้างหัวไฟล์เสร็จก่อน
      await getTicketImage();
      
      // 🟢 2. ย้ายคำสั่งส่ง MouseEvent มาดักเปิด Modal เฉพาะในกรณีที่เป็นรูปภาพจริงหรือกรณี noImg เท่านั้น
      // เพื่อเปิดทางให้ไฟล์ PDF (ดีดแท็บใหม่) และ Excel (ดาวน์โหลด) วิ่งผ่านไปได้โดยไม่หลุดเข้า Modal รูปแตก
      if (images.value.length > 0) {
        const targetFile = images.value[0];
        const fileType = targetFile.title || "";
        
        // ถ้าไม่ใช่ PDF และไม่ใช่ Excel แปลว่าเป็นรูปภาพชัวร์ หรือเป็นแถวภาพ noImg
        if (fileType !== 'application/pdf' && !fileType.includes('spreadsheetml') && !fileType.includes('excel')) {
          await nextTick();
          const modalBtn = document.getElementById('detailModal');
          if (modalBtn) {
            const clickEvent = new MouseEvent('click', {
              bubbles: false, // 🔥 ห้ามวิ่งทะลุขึ้นไปหาแท็กพ่อ <tr>
              cancelable: true
            });
            modalBtn.dispatchEvent(clickEvent);
          }
        }
      }
      
    } else {
      // 🟢 3. แผนสำรอง: กรณีที่ Record นี้ไม่มี Uid ไฟล์ผูกมาเลยแต่แรก
      let obj = {
        src: noImg,
        thumbnailImageSrc: noImg,
        alt: '',
        title: ''
      }
      images.value.push(obj)
      
      await nextTick();
      const modalBtn = document.getElementById('detailModal');
      if (modalBtn) {
        const clickEvent = new MouseEvent('click', {
          bubbles: false, // 🔥 ห้ามวิ่งทะลุขึ้นไปหาแท็กพ่อ <tr>
          cancelable: true
        });
        modalBtn.dispatchEvent(clickEvent);
      }
    }
  }
}

async function getTicketImage() {
  images.value = []
  // 1. กรองเอาเฉพาะรายการที่มี imageUid จริงๆ (ไม่เป็น "0")
  const validFiles = images_l.value.filter(file => file.imageUid && file.imageUid !== "0");

  if (validFiles.length > 0) {
    // 🟢 2. เปลี่ยนมาสร้าง Payload แบบ POST ยิงผ่าน Store ส่วนกลางที่คุณแก้ไขไว้
    const requests = validFiles.map((fileItem) => {
      const payload = {
        groupUid: key?.groupUid, // ส่งค่าว่าง หรือดึงจากตัวแปรกรุ๊ปของหน้านี้ (ถ้ามี)
        shopUid: key?.uid || "",  // ดึงจาก key.uid ตามสเปก Payload ดั้งเดิมของหน้านี้
        uid: String(fileItem.imageUid) // ไอดีของไฟล์แนบ
      };
      return stored.getFileFetch(payload); 
    });

    try {
      // 🟢 3. ยิงขนานขอข้อมูลทุกไฟล์ขึ้นมาพร้อมกันรวดเดียวด้วย Promise.all
      const allResults = await Promise.all(requests);
      
      if (allResults) {
        for (let i = 0; i < allResults.length; i++) {
          if (allResults[i] && allResults[i].status === 200 && allResults[i].data !== null) {
            const rawData = allResults[i].data;
            const isBlob = rawData instanceof Blob || (rawData && rawData.size !== undefined);
            
            if (isBlob) {
              // สแกนตรวจสอบเนื้อไฟล์จริง (Magic Numbers) ของไฟล์ดิบชิ้นนี้
              const textHead = await rawData.slice(0, 4).text();
              let cleanBlob;
              let fileType;

              if (textHead === "%PDF") {
                cleanBlob = new Blob([rawData], { type: 'application/pdf' });
                fileType = 'application/pdf';
              } else if (textHead.startsWith("PK")) {
                cleanBlob = new Blob([rawData], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
              } else {
                // 🖼️ สลัดหัวดาวน์โหลดทิ้ง บังคับเปลี่ยนหัวเป็นภาพใสๆ เพื่อแก้ปัญหารูปแตกบนบราวเซอร์
                cleanBlob = new Blob([rawData], { type: 'image/jpeg' });
                fileType = 'image/jpeg';
              }

              const dataUrl = URL.createObjectURL(cleanBlob);
              const originalItem = validFiles[i];

              images.value.push({
                src: dataUrl,
                thumbnailImageSrc: dataUrl,
                alt: '',
                title: fileType, 
                filename: 'attachment_' + originalItem.imageUid, 
                uid: originalItem.uid,
                imageUid: originalItem.imageUid,
                rawBlob: cleanBlob 
              });
            }
          }
        }
      }
    } catch (e) {
      console.error("Error inside Promise.all in getTicketImage:", e);
      return;
    }
  }

  // 🟢 4. ส่วนคัดแยกประเภทเส้นทางการเรนเดอร์เนื้อหาไฟล์ให้ถูกต้อง
  if (images.value.length > 0) {
    const targetFile = images.value[0]; // หยิบไฟล์ตัวแรกมาเช็คทิศทาง
    const fileType = targetFile.title || "";
    const fileSrc = targetFile.src || "";

    if (fileType === 'application/pdf') {
      // 📄 กรณีเป็น PDF -> ดีดเปิดแท็บใหม่ทันที
      window.open(fileSrc, '_blank');

    } else if (fileType.includes('spreadsheetml') || fileType.includes('excel')) {
      // 📊 กรณีเป็น Excel -> สั่งดาวน์โหลดลงเครื่องพร้อมเปลี่ยนชื่อให้อ่านง่าย
      let today = moment(new Date()).local().format('YYYYMMDD_HHmmss');
      let fileNameDownload = `excel_${today}.xlsx`;
      
      const link = document.createElement('a');
      link.href = fileSrc;
      link.setAttribute('download', fileNameDownload);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

    } else {
      // 🖼️ กรณีเป็นรูปภาพปกติ -> สั่งเปิดกล่องป๊อปอัป Modal หน้านี้ตามเดิม
      await nextTick();
      document.getElementById("detailModal").click();
    }

  } else {
    // 🟢 5. แผนสำรอง: กรณีที่เปิดขึ้นมาแล้วไม่มีไฟล์แนบค้างอยู่จริง
    // ให้สวมภาพ noImg แทนที่ และเปิด Modal แสดงผลตามระเบียบฟลูเดิมของคุณ
    images.value.push({
      src: noImg,
      thumbnailImageSrc: noImg,
      alt: '',
      title: ''
    });
    await nextTick();
    document.getElementById("detailModal").click();
  }
}



async function getworkCaseInfo(refNumber) {
  let data = "";
  data = {
    groupUid: key.groupUid,
    shopUid: key.uid,
    refNumber: refNumber,
  };
  const payload = data;
  // console.log("workCaseInfo p : " + JSON.stringify(payload));
  const response = await storewc.workCaseInfoByRefFetch(payload);
  // console.log("getworkCaseInfo : " + JSON.stringify(response.data));
  if (response) {
    if (response.status === 200) {
      if (response.data.rtnDesc === "success") {
        workcaseDetail.value = response.data.data
      }
    }
  }
}

async function getworkCaseInfoPrv(prevRefNumber) {
  let workcase_prv = null
  let data = "";
  data = {
    groupUid: key.groupUid,
    shopUid: key.uid,
    refNumber: prevRefNumber,
  };
  const payload = data;

  const response = await storewc.workCaseInfoByRefFetch(payload);
  //  console.log("workCaseInfoByRef : " + JSON.stringify(response.data));
  if (response) {
    if (response.status === 200) {
      if (response.data.rtnDesc === "success") {
        workcase_prv = response.data.data
        bf_changename.value = workcase_prv.tkCustomerInfo.name 
        transdate_prv.value = workcase_prv.workItems[0].startTime
      }
    }
  }
}


async function getDepositInfo(tkRef) {
  let data = "";
  data = {
    index: 0,
    size: 1,
    groupUid: key.groupUid,
    shopUid: key.uid,
    cusUid: '',
    refNumber: '',
    tkRefNumber: tkRef.trim(),
    channel: '',
    txDate: 0,
  };
  const payload = data;
  // console.log("ld p : " + JSON.stringify(payload));
  const response = await storedepo.listDepositFetch(payload);
  // console.log("ld r : " + JSON.stringify(response.data));
  if (response) {
    if (response.status === 200) {
      if (response.data.rtnDesc === "success") {
        let listd = response.data.data.list
        dep_ref.value = listd[0].refNumber
      }
    }
  }
}

async function retrieveInfo(item) {
  promotionDetail.value = null
  let data = "";
  let pawnValue = 0
  if (item.transactionType != 'REDEEM') {
      pawnValue = item.newPawnValue
  } else {
      pawnValue = item.pawnValue
  }

  let cusUid = item.customerInfo.customerUid

  if(item.represent || item.shopRepresent){
     cusUid =  item.tkCustomerInfo.customerUid 
  }

  data = {
    cusUid : cusUid,
    pawnDate: moment(item.workItems[0].startTime).format('YYYY-MM-DD'),
    previousTxDate: '',
    pawnValue: pawnValue,
  };
  const payload = data;
  // console.log("calIntInt p : " + JSON.stringify(payload));
  const response = await storei.interestRetrieveFetch(payload);
  // console.log("retrieveInfo r : " + JSON.stringify(response.data));
  let res = ''
  if (response) {
    if (response.status === 200) {
      if (response.data.rtnDesc === "success") {
        res = response.data.data
        const result = res.interests.flatMap(item => {
        // เงื่อนไข: เป็นโปรโมชัน และ periodMonths > 1
        if (item.promotion === true && item.periodMonths > 1) {
          // สร้าง Array ใหม่ที่มีจำนวน element เท่ากับ periodMonths และใส่ข้อมูล item ลงไป
          return Array.from({ length: item.periodMonths }, () => ({ ...item }));
        }
        // หากไม่ตรงเงื่อนไข ให้คืนค่าเดิมกลับไป 1 ชุด
        return [item];
        });
        // console.log('result : ' + JSON.stringify(result));
      res.interests  =  result
      promotionDetail.value = res
      // console.log('promotionDetail : ' + JSON.stringify(promotionDetail.value));
      }
    }
  }
}

async function calIntInt() {
  // console.log('workcaseDetail : ' + JSON.stringify(workcaseDetail.value));
  let postponeDate = ''
  if (workcaseDetail.value.postponeCount != 0) {
    postponeDate = moment(workcaseDetail.value.postponeDate).format('YYYY-MM-DD')
  }
  let data = "";
  let pawnValue = 0
  if (workcaseDetail.value.transactionType != 'REDEEM') {
    pawnValue = workcaseDetail.value.newPawnValue
  } else {
    pawnValue = workcaseDetail.value.pawnValue
  }

  let cusUid = workcaseDetail.value.customerInfo.customerUid

  if(workcaseDetail.value.represent || workcaseDetail.value.shopRepresent){
     cusUid =  workcaseDetail.value.tkCustomerInfo.customerUid 
  }
  data = {
    cusUid: cusUid,
    txType: workcaseDetail.value.transactionType,
    pawnDate: moment(workcaseDetail.value.workItems[0].startTime).format('YYYY-MM-DD'),
    previousTxDate: '',
    postponeDate: postponeDate,
    pawnValue: pawnValue,
    promotionUid: workcaseDetail.value.promotionUid,
    interestUid: workcaseDetail.value.interestUid,
  };
  const payload = data;
  // console.log("calIntInt p : " + JSON.stringify(payload));
  const response = await storei.interestCalculateFetch(payload);
  // console.log("calIntInt r : " + JSON.stringify(response.data));
  let res = ''
  if (response) {
    if (response.status === 200) {
      if (response.data.rtnDesc === "success") {
        res = response.data.data
        int_cal.value = Number(res.overdueInterest) + Number(res.dueInterest)
      }
    }
  }
}

async function detailPayment(item) {
  // console.log('detailPayment : ' + item);
  let data = "";
  data = {
    groupUid: key.groupUid,
    shopUid: key.uid,
    uid: item,
  };
  const payload = data;
  const response = await storep.paymentGroupInfoAdminFetch(payload);
  // console.log("detailPayment : " + JSON.stringify(response.data));
  if (response) {
    if (response.status === 200) {
      if (response.data.rtnDesc === "success") {
        paymentE.value = response.data.data
        filterPayType()
        // console.log('detailPayment : ' + JSON.stringify(paymentE.value));
      }
    }
  }
}




function filterPayType() {
  payment_l.value = []
  let pt = [{ name: 'CASH' },
  { name: 'BANK_TRANSFER' },
  { name: 'CREDIT_CARD' },
  { name: 'CHEQUE' },
  { name: 'PROMPT_PAY_MOBILE' },
  { name: 'PROMPT_PAY_TAX_ID' },
  { name: 'QR_CODE' },
  ]
  let lsum = []
  for (let i = 0; i < pt.length; i++) {
    lsum = filterSumPay(pt[i].name)
    if (lsum.length > 0) {
      payment_l.value.push({ name: pt[i].name, pay: lsum[0].pay, receive: lsum[0].receive, fee: lsum[0].fee })
    } else {
      payment_l.value.push({ name: pt[i].name, pay: 0, receive: 0, fee: 0 })
    }
  }
}

function filterSumPay(str) {
  let nump = 0, numr = 0, numf = 0
  let lp = paymentE.value.payments.filter((c) => c.type === str);
  if (lp.length > 0) {
    let pay = 0
    let receive = 0
    let fee = 0
    for (let i = 0; i < lp.length; i++) {
      pay = pay + lp[i].pay
      receive = receive + lp[i].receive
      fee = fee + lp[i].feeReceive
      // fee = fee + lp[i].fee
    }
    nump = pay
    numr = receive
    numf = fee
  }
  let lockL = []
  lockL.push({ pay: nump, receive: numr, fee: numf })
  // console.log('lockL : ' + JSON.stringify(lockL));
  return lockL
}


function getFileFromBase64(string64, fileName) {
  const trimmedString = string64.replace('dataimage/jpegbase64', '');
  // const trimmedString = string64;
  const imageContent = atob(trimmedString);
  const buffer = new ArrayBuffer(imageContent.length);
  const view = new Uint8Array(buffer);

  for (let n = 0; n < imageContent.length; n++) {
    view[n] = imageContent.charCodeAt(n);
  }
  var ext = fileName.split('.').pop();
  let type = '';
  if (ext === 'jpg') {
    type = 'image/jpeg'
  } else if (ext === 'png') {
    type = 'image/png'
  }
  const blob = new Blob([buffer], { type });
  return new File([blob], fileName, { lastModified: new Date().getTime(), type });
}

function checkTransaction(str) {
  // PAWN, INTEREST, REDEEM, INC_PRINCIPLE, DEC_PRINCIPLE
  let side = ''
  if (str === 'PAWN') {
    side = 'จำนำ'
  } else if (str === 'INTEREST') {
    side = 'ต่อดอกเบี้ย'
  } else if (str === 'REDEEM') {
    side = 'ไถ่ถอน'
  } else if (str === 'INC_PRINCIPLE') {
    side = 'เพิ่มต้น'
  } else if (str === 'DEC_PRINCIPLE') {
    side = 'ลดต้น'
  }
  return side
}

function copyBtn() {
  navigator.clipboard.writeText(workcaseDetail.value.tkRefNumber);
}

const NewTab = async () => {
  sessionStorage.removeItem('deferWorkcaseUid');
  sessionStorage.setItem("deferWorkcaseUid", workcaseDetail.value.uid);
  if (sessionStorage.getItem('deferWorkcaseUid') !== null) {
    const win = window.open("#/generals/defer_items", "_blank");
    win.focus();
    sessionStorage.removeItem('deferWorkcaseUid');
  }
}

onMounted(() => {
  loadAssetTypes();
  loadData();
});
</script>

<style lang="scss" scoped>
.loading-state {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.3);
  z-index: 9999;
  display: flex;
  justify-content: center;
  align-items: center;
}
</style>

<!-- Pager look, matching the CSS the other two frontends pull in from @/examples/ExampleOne.vue —
     this app has no vue-awesome-paginate, so the classes are applied to hand-rolled buttons instead.
     Unscoped on purpose — it only targets .example-one, which is where the pager lives. -->
<style>
.example-one .pagination-container {
  display: flex;
  align-items: center;
  column-gap: 10px;
}

.example-one .paginate-buttons {
  height: 40px;
  width: 40px;
  border-radius: 20px;
  cursor: pointer;
  background-color: rgb(242, 242, 242);
  border: 1px solid rgb(217, 217, 217);
  color: black;
}

.example-one .paginate-buttons:hover {
  background-color: #d8d8d8;
}

.example-one .paginate-buttons:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.example-one .active-page {
  background-color: #08ac54;
  border: 1px solid #08ac44;
  color: white;
}

.example-one .active-page:hover {
  background-color: #08ac54;
}

.example-one .back-button:active,
.example-one .next-button:active {
  background-color: #c4c4c4;
}

.example-one .paginate-ellipsis {
  width: 20px;
  text-align: center;
  color: rgb(153, 153, 153);
}
</style>
